import { Button, CopyButton, StatusBadge } from '../../components/core';
import { type Column } from '../../components/feedback';
import type { AdminMonitoringRowDto } from '@livestreamx/shared-types';
import { ReportList } from './ReportList';

/** Session bytes in the CSV stay raw; only the table cell is humanised. */
const fmtBytes = (b: number): string => {
  const u = ['B', 'KB', 'MB', 'GB'];
  let v = b;
  let i = 0;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${i === 0 ? v : v.toFixed(1)} ${u[i]}`;
};

const fmtKbps = (k: number | null): string =>
  k === null ? '—' : k >= 1000 ? `${(k / 1000).toFixed(1)} Mbps` : `${k.toLocaleString('en-IN')} kbps`;

const when = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '—';

const columns: Column<AdminMonitoringRowDto>[] = [
  { key: 'tenant', header: 'Tenant', render: (r) => <span className="text-muted text-sm">{r.tenantName}</span> },
  {
    key: 'stream',
    header: 'Stream',
    render: (r) => (
      <div>
        <div className="font-medium">{r.name}</div>
        {/* Key-less public ingest URL, same convention as the tenant detail page. */}
        <div className="url-cell">
          <code>{r.rtmpUrl}</code>
          <CopyButton text={r.rtmpUrl} />
        </div>
      </div>
    ),
  },
  // MediaMTX-confirmed live-ness, not the stored column — this page shows what
  // is on air right now.
  { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.live ? 'LIVE' : 'OFFLINE'} /> },
  { key: 'in', header: 'In (ingest)', render: (r) => <span>{fmtKbps(r.inputKbps)}</span> },
  { key: 'out', header: 'Out (egress)', render: (r) => <span>{fmtKbps(r.outputKbps)}</span> },
  { key: 'viewers', header: 'Viewers', render: (r) => <span>{r.live ? r.viewers.toLocaleString('en-IN') : '—'}</span> },
  { key: 'per-viewer', header: 'Per viewer', render: (r) => <span>{fmtKbps(r.perViewerKbps)}</span> },
  {
    key: 'session',
    header: 'Session',
    render: (r) => (
      <div className="text-sm">
        <div>{r.live ? `${fmtBytes(r.upBytes + r.downBytes)} up + down` : '—'}</div>
        <div className="text-muted text-xs">since {when(r.sessionStartedAt)}</div>
      </div>
    ),
  },
  { key: 'peak', header: 'Peak', render: (r) => <span>{fmtKbps(r.peakKbps)}</span> },
];

const STATUS_FILTER = [
  { value: '', label: 'All streams' },
  { value: 'live', label: 'Live now' },
  { value: 'offline', label: 'Offline' },
];

export function AdminMonitoringPage() {
  return (
    <ReportList<AdminMonitoringRowDto>
      title="Stream monitoring"
      subtitle="Every tenant stream URL with live inbound/outbound bitrate and per-viewer bandwidth"
      endpoint="/api/admin/monitoring"
      searchPlaceholder="Search tenants, streams or URLs…"
      filters={[{ key: 'status', label: 'Live status', options: STATUS_FILTER }]}
      columns={columns}
      emptyText="No streams yet."
      // Live numbers are worthless stale — re-poll so the bitrate columns keep
      // tracking the MediaMTX samplers without a manual reload.
      autoRefreshMs={15_000}
      actions={<Button variant="secondary" onClick={() => window.location.reload()}>⟳ Refresh</Button>}
      csv={{
        filename: 'stream-monitoring.csv',
        header: [
          'Tenant',
          'Stream',
          'RTMP URL',
          'Live',
          'Input kbps',
          'Output kbps',
          'Current kbps',
          'Peak kbps',
          'Up bytes',
          'Down bytes',
          'Viewers',
          'Per-viewer kbps',
          'Session started',
        ],
        toRow: (r) => [
          r.tenantName,
          r.name,
          r.rtmpUrl,
          r.live ? 'LIVE' : 'OFFLINE',
          r.inputKbps,
          r.outputKbps,
          r.currentKbps,
          r.peakKbps,
          r.upBytes,
          r.downBytes,
          r.viewers,
          r.perViewerKbps,
          r.sessionStartedAt,
        ],
      }}
    />
  );
}